import type { JwtPayload } from '@control-escolar/shared/types';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../api/client';

interface AuthState {
  token: string | null;
  user: JwtPayload | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  mustChangePassword: boolean;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  superAdminLogin: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  clearMustChangePassword: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function decodeToken(token: string): JwtPayload | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return {
      userId: decoded.userId || '',
      email: decoded.email || '',
      name: decoded.name || null,
      role: decoded.role || '',
      schoolId: decoded.schoolId || '',
      schoolSlug: decoded.schoolSlug || '',
      schoolName: decoded.schoolName || '',
      schoolModules: decoded.schoolModules || {},
      schoolBrand: decoded.schoolBrand || {},
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: localStorage.getItem('auth-token'),
    user: null,
    isAuthenticated: false,
    isSuperAdmin: false,
    mustChangePassword: localStorage.getItem('must-change-password') === 'true',
    loading: true,
  });

  const updateFromToken = useCallback((token: string | null) => {
    if (!token) {
      localStorage.removeItem('must-change-password');
      setState({
        token: null,
        user: null,
        isAuthenticated: false,
        isSuperAdmin: false,
        mustChangePassword: false,
        loading: false,
      });
      return;
    }
    const user = decodeToken(token);
    if (!user) {
      localStorage.removeItem('auth-token');
      localStorage.removeItem('must-change-password');
      setState({
        token: null,
        user: null,
        isAuthenticated: false,
        isSuperAdmin: false,
        mustChangePassword: false,
        loading: false,
      });
      return;
    }
    const mustChangePassword = localStorage.getItem('must-change-password') === 'true';
    setState({
      token,
      user,
      isAuthenticated: true,
      isSuperAdmin: user.role === 'SUPER_ADMIN',
      mustChangePassword,
      loading: false,
    });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('auth-token');
    updateFromToken(token);
  }, [updateFromToken]);

  const login = useCallback(
    async (email: string, password: string) => {
      const data = await api.post<{ token: string; mustChangePassword: boolean; error?: string }>(
        '/api/auth/login',
        { email, password },
      );

      const token = data.token;
      if (token) {
        const mustChange = !!data.mustChangePassword;
        localStorage.setItem('auth-token', token);
        if (mustChange) localStorage.setItem('must-change-password', 'true');
        else localStorage.removeItem('must-change-password');
        updateFromToken(token);
      }
    },
    [updateFromToken],
  );

  const superAdminLogin = useCallback(
    async (email: string, password: string) => {
      const data = await api.post<{ token: string; error?: string }>(
        '/api/auth/super-admin/login',
        { email, password },
      );

      const token = data.token;
      if (token) {
        localStorage.setItem('auth-token', token);
        updateFromToken(token);
      }
    },
    [updateFromToken],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Ignore network errors during logout
    }
    localStorage.removeItem('auth-token');
    localStorage.removeItem('must-change-password');
    setState({
      token: null,
      user: null,
      isAuthenticated: false,
      isSuperAdmin: false,
      mustChangePassword: false,
      loading: false,
    });
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('auth-token');
    updateFromToken(token);
  }, [updateFromToken]);

  const clearMustChangePassword = useCallback(() => {
    localStorage.removeItem('must-change-password');
    setState((prev) => ({ ...prev, mustChangePassword: false }));
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...state, login, superAdminLogin, logout, refresh, clearMustChangePassword }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
